/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { ITabItems, IGeoRegionFilterProps } from "./types";
import { useStoreDispatch } from "../../../store/hooks";
import { resetKeyword } from "../../../store/search/reducer";
import { useSelector } from "react-redux";
import { setSelectedType } from "../../../store/api/devices/reducers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { useLocation } from "react-router-dom";
import { resetEventFilters } from "../../../store/filters/events/EventFilterReducer";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";

const PanelTabs = (props: {
  tabData: Array<ITabItems>,
  variant?: "dashboard" | "default",
  geoRegionFilterProps?: IGeoRegionFilterProps,
  children?: React.ReactNode
}) => {
  const { tabData, variant = "default" } = props;
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const [value, setValue] = React.useState(0);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const deviceType = location?.pathname?.split("/")[3];

  // Build filtered tab list respecting permissions
  const tabDisplayData: ITabItems[] = React.useMemo(() => {
    return tabData.filter((item) => {
      if (item?.permissionRequired && !permissions?.hasOpCodeGroup(item?.permissionRequired)) {
        return false;
      }
      return true;
    });
  }, [tabData, permissions]);

  const handleChange = (newIndex: string) => {
    const idx = Number(newIndex);
    const item = tabDisplayData[idx];
    const tempLabel = item?.label?.toLowerCase() || "";

    if (tempLabel.includes("alarms")) {
      dispatch(setSelectedType("alarms"));
    } else if (tempLabel.includes("local")) {
      dispatch(setSelectedType("localTab"));
    } else {
      dispatch(setSelectedType("none"));
    }
    dispatch(resetKeyword());
    dispatch(resetEventFilters());
    setValue(idx);
  };

  React.useEffect(() => {
    dispatch(setSelectedType("alarms"));
  }, [dispatch]);

  React.useEffect(() => {
    if (deviceType !== "performance") {
      setValue(0);
    }
  }, [deviceType]);

  React.useEffect(() => {
    if (tabDisplayData.length === 1) setValue(0);
  }, [tabDisplayData.length]);

  return (
    <div className="w-full">
      <Tabs value={String(value)} onValueChange={handleChange}>
        <div className="flex justify-between items-center w-full border-b border-border">
          <TabsList className="inline-flex h-auto gap-0 rounded-none border-0 bg-transparent p-0 text-muted-foreground shrink-0">
            {tabDisplayData.map((item: ITabItems, index: number) => (
              <TabsTrigger
                key={index}
                value={String(index)}
                className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                <span className="flex items-center gap-1.5">
                  {item.iconPosition === "start" && item.icon}
                  {item.label}
                  {(item.iconPosition === "end" || !item.iconPosition) && item.icon}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
      </Tabs>
      {props.children}
      {tabDisplayData.map((item: ITabItems, index: number) => (
        <div
          key={index}
          role="tabpanel"
          hidden={value !== index}
          id={`simple-tabpanel-${index}`}
          aria-labelledby={`simple-tab-${index}`}
        >
          {value === index && <div className="pt-4">{item.component}</div>}
        </div>
      ))}
    </div>
  );
};

export default PanelTabs;
